import React, { useState } from 'react';
import { Plus, Trash2, Move, Target } from 'lucide-react';
import { Resource } from '../../../types';

interface ObjectManipulatorConfigFormProps {
  resource: Resource;
  config?: Record<string, any>;
  onConfigChange: (config: Partial<Resource>) => void;
}

interface ManipulableObject {
  id: string;
  type: string;
  color: string;
  size: string;
  initialPosition: { x: number; y: number };
  initialRotation: number;
  initialScale: number;
}

interface Transformation {
  type: 'move' | 'rotate' | 'scale';
  target: string;
  instruction: string;
  expectedValue?: any;
}

export const ObjectManipulatorConfigForm: React.FC<ObjectManipulatorConfigFormProps> = ({
  resource,
  config,
  onConfigChange
}) => {
  const mergedConfig = { ...resource, ...config };
  const [objects, setObjects] = useState<ManipulableObject[]>(mergedConfig.objects || []);
  const [transformations, setTransformations] = useState<Transformation[]>(mergedConfig.transformations || []);
  const [allowInteraction, setAllowInteraction] = useState<boolean>(mergedConfig.allowInteraction !== undefined ? mergedConfig.allowInteraction : true);
  const [question, setQuestion] = useState<string>(mergedConfig.question || '');
  const [showInstructions, setShowInstructions] = useState<boolean>(mergedConfig.showInstructions !== undefined ? mergedConfig.showInstructions : true);
  const [gridSize, setGridSize] = useState<string>(mergedConfig.gridSize || '10x10');

  const availableShapes = ['circle', 'square', 'triangle', 'star', 'diamond', 'hexagon'];
  const availableColors = ['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'pink', 'brown'];
  const availableSizes = ['small', 'medium', 'large'];
  const gridSizes = ['5x5', '8x8', '10x10', '12x12', '15x15'];

  // Remove the problematic useEffect that was causing infinite loops
  // Instead, we'll call onConfigChange only when explicitly needed

  const addObject = () => {
    const newObject: ManipulableObject = {
      id: `obj_${Date.now()}`,
      type: 'circle',
      color: 'red',
      size: 'medium',
      initialPosition: { x: 50, y: 50 },
      initialRotation: 0,
      initialScale: 1
    };
    const newObjects = [...objects, newObject];
    setObjects(newObjects);
    onConfigChange({
      ...resource,
      objects: newObjects
    });
  };

  const updateObject = (index: number, field: keyof ManipulableObject, value: any) => {
    const updatedObjects = [...objects];
    updatedObjects[index] = { ...updatedObjects[index], [field]: value };
    setObjects(updatedObjects);
    onConfigChange({
      ...resource,
      objects: updatedObjects
    });
  };

  const removeObject = (index: number) => {
    const newObjects = objects.filter((_, i) => i !== index);
    setObjects(newObjects);
    onConfigChange({
      ...resource,
      objects: newObjects
    });
  };

  const addTransformation = () => {
    const newTransformation: Transformation = {
      type: 'move',
      target: objects.length > 0 ? objects[0].id : '',
      instruction: 'Déplacez l\'objet vers la position demandée'
    };
    const newTransformations = [...transformations, newTransformation];
    setTransformations(newTransformations);
    onConfigChange({
      ...resource,
      transformations: newTransformations
    });
  };

  const updateTransformation = (index: number, field: keyof Transformation, value: any) => {
    const updatedTransformations = [...transformations];
    updatedTransformations[index] = { ...updatedTransformations[index], [field]: value };
    setTransformations(updatedTransformations);
    onConfigChange({
      ...resource,
      transformations: updatedTransformations
    });
  };

  const removeTransformation = (index: number) => {
    const newTransformations = transformations.filter((_, i) => i !== index);
    setTransformations(newTransformations);
    onConfigChange({
      ...resource,
      transformations: newTransformations
    });
  };

  const getColorValue = (colorName: string) => {
    const colorMap: Record<string, string> = {
      'red': '#EF4444',
      'blue': '#3B82F6',
      'green': '#10B981',
      'yellow': '#F59E0B',
      'purple': '#8B5CF6',
      'orange': '#F97316',
      'pink': '#EC4899',
      'brown': '#A0522D'
    };
    return colorMap[colorName] || colorName;
  };

  const getShapeIcon = (shape: string) => {
    const iconMap: Record<string, string> = {
      'circle': '●',
      'square': '■',
      'triangle': '▲',
      'star': '⭐',
      'diamond': '◆',
      'hexagon': '⬡'
    };
    return iconMap[shape] || '●';
  };

  const getTransformationIcon = (type: string) => {
    const iconMap: Record<string, string> = {
      'move': '↔️',
      'rotate': '🔄',
      'scale': '📏'
    };
    return iconMap[type] || '•';
  };

  const getTransformationLabel = (type: string) => {
    const labelMap: Record<string, string> = {
      'move': 'Déplacement',
      'rotate': 'Rotation',
      'scale': 'Mise à l\'échelle'
    };
    return labelMap[type] || type;
  };

  const getGridDimensions = () => {
    const [rows, cols] = gridSize.split('x').map(Number);
    return { rows, cols };
  };

  const { rows, cols } = getGridDimensions();

  // Ensure all objects have required properties
  const normalizedObjects = objects.map(obj => ({
    ...obj,
    size: obj.size || 'medium',
    initialPosition: obj.initialPosition || { x: 50, y: 50 },
    initialRotation: obj.initialRotation || 0,
    initialScale: obj.initialScale || 1
  }));

  return (
    <div className="space-y-6">
      {/* Question Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou consigne
        </label>
        <textarea
          value={question}
          onChange={(e) => {
            const newQuestion = e.target.value;
            setQuestion(newQuestion);
            onConfigChange({
              ...resource,
              question: newQuestion
            });
          }}
          placeholder="Ex: Manipulez les objets selon les instructions données"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
        />
      </div>

      {/* Grid Configuration */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
          <Target className="w-5 h-5 mr-2" />
          Configuration de la grille
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Taille de la grille
          </label>
          <select
            value={gridSize}
            onChange={(e) => {
              const newGridSize = e.target.value;
              setGridSize(newGridSize);
              onConfigChange({
                ...resource,
                gridSize: newGridSize
              });
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {gridSizes.map(size => (
              <option key={size} value={size}>
                {size} ({size.split('x')[0]} × {size.split('x')[1]})
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Grille {rows} × {cols} cellules pour le positionnement des objets
          </p>
        </div>
      </div>

      {/* Objects Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Objets à manipuler
          </h3>
          <button
            type="button"
            onClick={addObject}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un objet</span>
          </button>
        </div>

        <div className="space-y-3">
          {normalizedObjects.map((obj, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Objet {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeObject(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Shape */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Forme
                  </label>
                  <select
                    value={obj.type}
                    onChange={(e) => updateObject(index, 'type', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableShapes.map(shape => (
                      <option key={shape} value={shape}>
                        {shape}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur
                  </label>
                  <select
                    value={obj.color}
                    onChange={(e) => updateObject(index, 'color', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableColors.map(color => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Size */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Taille
                  </label>
                  <select
                    value={obj.size}
                    onChange={(e) => updateObject(index, 'size', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableSizes.map(size => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Initial Position */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Position initiale
                  </label>
                  <div className="grid grid-cols-2 gap-1">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={obj.initialPosition?.x || 0}
                      onChange={(e) => updateObject(index, 'initialPosition', { 
                        ...obj.initialPosition, 
                        x: parseInt(e.target.value) || 0 
                      })}
                      placeholder="X"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={obj.initialPosition?.y || 0}
                      onChange={(e) => updateObject(index, 'initialPosition', { 
                        ...obj.initialPosition, 
                        y: parseInt(e.target.value) || 0 
                      })}
                      placeholder="Y"
                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Initial Transformations */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Rotation initiale
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="360"
                    value={obj.initialRotation || 0}
                    onChange={(e) => updateObject(index, 'initialRotation', parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">degrés</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Échelle initiale
                  </label>
                  <input
                    type="number"
                    min="0.1"
                    max="3"
                    step="0.1"
                    value={obj.initialScale || 1}
                    onChange={(e) => updateObject(index, 'initialScale', parseFloat(e.target.value) || 1)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">facteur</p>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Aperçu:</span>
                <div
                  className="w-6 h-6 border border-gray-300 rounded flex items-center justify-center text-sm"
                  style={{ 
                    backgroundColor: getColorValue(obj.color),
                    transform: `rotate(${obj.initialRotation || 0}deg) scale(${obj.initialScale || 1})`
                  }}
                >
                  {getShapeIcon(obj.type)}
                </div>
                <span className="text-xs text-gray-600">
                  à ({obj.initialPosition?.x || 0}, {obj.initialPosition?.y || 0})
                </span>
              </div>
            </div>
          ))}
        </div>

        {normalizedObjects.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Target className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Aucun objet configuré</p>
            <p className="text-sm">Ajoutez des objets pour les faire manipuler</p>
          </div>
        )}
      </div>

      {/* Transformations Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Move className="w-5 h-5 mr-2" />
            Instructions de transformation
          </h3>
          <button
            type="button"
            onClick={addTransformation}
            disabled={objects.length === 0}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une instruction</span>
          </button>
        </div>

        <div className="space-y-3">
          {transformations.map((transformation, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Instruction {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeTransformation(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Transformation Type */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Type de transformation
                  </label>
                  <select
                    value={transformation.type}
                    onChange={(e) => updateTransformation(index, 'type', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="move">Déplacement</option>
                    <option value="rotate">Rotation</option>
                    <option value="scale">Mise à l'échelle</option>
                  </select>
                </div>

                {/* Target Object */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Objet cible
                  </label>
                  <select
                    value={transformation.target}
                    onChange={(e) => updateTransformation(index, 'target', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner un objet</option>
                    {objects.map(obj => (
                      <option key={obj.id} value={obj.id}>
                        {obj.type} {obj.color}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Instruction */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Instruction
                  </label>
                  <input
                    type="text"
                    value={transformation.instruction}
                    onChange={(e) => updateTransformation(index, 'instruction', e.target.value)}
                    placeholder="Ex: Déplacez vers le centre"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Preview */}
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Aperçu:</span>
                <span className="text-lg">{getTransformationIcon(transformation.type)}</span>
                <span className="text-xs text-gray-600">
                  {getTransformationLabel(transformation.type)}: {transformation.instruction}
                </span>
                {transformation.target && (
                  <span className="text-xs text-gray-500">
                    sur {objects.find(o => o.id === transformation.target)?.type}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {transformations.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            <Move className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>Aucune instruction configurée</p>
            <p className="text-sm">Ajoutez des instructions pour guider les élèves</p>
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Paramètres</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Permettre l'interaction
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={allowInteraction}
                onChange={(e) => {
                  const newAllowInteraction = e.target.checked;
                  setAllowInteraction(newAllowInteraction);
                  onConfigChange({
                    ...resource,
                    allowInteraction: newAllowInteraction
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {allowInteraction ? 'Oui' : 'Non'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Permettre aux élèves de manipuler les objets
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Afficher les instructions
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={showInstructions}
                onChange={(e) => {
                  const newShowInstructions = e.target.checked;
                  setShowInstructions(newShowInstructions);
                  onConfigChange({
                    ...resource,
                    showInstructions: newShowInstructions
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {showInstructions ? 'Oui' : 'Non'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Afficher les instructions aux élèves
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Résumé de la configuration</h4>
        <div className="text-xs text-blue-800 space-y-1">
          <p>• Grille: {gridSize} ({rows} × {cols} cellules)</p>
          <p>• {objects.length} objet(s) à manipuler</p>
          <p>• {transformations.length} instruction(s) de transformation</p>
          <p>• Instructions affichées: {showInstructions ? 'Oui' : 'Non'}</p>
          {question && <p>• Question: "{question}"</p>}
        </div>
      </div>
    </div>
  );
};
